import Phaser from 'phaser'
import { useGameStore } from '../stores/gameStore'
import { networkClient } from '../network/client'

const TILE_WIDTH = 64
const TILE_HEIGHT = 32
const MAP_WIDTH = 20
const MAP_HEIGHT = 15
const PLAYER_SPEED = 200

interface PlayerSprite extends Phaser.GameObjects.Sprite {
  playerId: string
  targetX: number
  targetY: number
  isAI?: boolean
}

interface PuzzleMarker extends Phaser.GameObjects.Container {
  markerId: string
  puzzleType: 'pattern' | 'sequence' | 'logic' | 'debug'
  difficulty: 'easy' | 'medium' | 'hard'
  gridX: number
  gridY: number
  isSolved: boolean
  isNearby: boolean
}

export class GameScene extends Phaser.Scene {
  private player: PlayerSprite | null = null
  private otherPlayers: Map<string, PlayerSprite> = new Map()
  private cursors: Phaser.Types.Input.Keyboard.CursorKeys | null = null
  private wasd: { W: Phaser.Input.Keyboard.Key; A: Phaser.Input.Keyboard.Key; S: Phaser.Input.Keyboard.Key; D: Phaser.Input.Keyboard.Key } | null = null
  private lastSentPosition = { x: 0, y: 0 }
  private sendPositionTimer = 0
  private puzzleMarkers: PuzzleMarker[] = []
  private mapOffsetX = 0
  private mapOffsetY = 0

  // AI companion selection
  private selectedAI: string | null = null
  private aiNumberKeys: { one: Phaser.Input.Keyboard.Key; two: Phaser.Input.Keyboard.Key; three: Phaser.Input.Keyboard.Key } | null = null

  constructor() {
    super({ key: 'GameScene' })
  }

  create() {
    // Create isometric floor
    this.createFloor()

    // Setup keyboard input
    this.cursors = this.input.keyboard?.createCursorKeys() ?? null
    this.wasd = {
      W: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.W),
      A: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      S: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      D: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.D),
    }

    // AI selection keys (1, 2, 3)
    this.aiNumberKeys = {
      one: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.ONE),
      two: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.TWO),
      three: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.THREE),
    }

    // Setup right-click handler for map
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown()) {
        this.handleRightClick(pointer)
      }
    })

    // Disable browser context menu on canvas
    this.input.mouse?.disableContextMenu()

    // Create player based on role
    this.createLocalPlayer()

    // Sync with other players
    this.setupNetworkSync()

    // Camera follow player
    if (this.player) {
      this.cameras.main.startFollow(this.player, true, 0.1, 0.1)
      this.cameras.main.setZoom(1.5)
    }
  }

  createFloor() {
    // Create a simple isometric grid
    this.mapOffsetX = this.cameras.main.width / 2
    this.mapOffsetY = 100

    for (let y = 0; y < MAP_HEIGHT; y++) {
      for (let x = 0; x < MAP_WIDTH; x++) {
        // Convert to isometric coordinates
        const isoX = (x - y) * (TILE_WIDTH / 2) + this.mapOffsetX
        const isoY = (x + y) * (TILE_HEIGHT / 2) + this.mapOffsetY

        // Add floor tile
        const tile = this.add.image(isoX, isoY, 'tile_floor')
        tile.setDepth(0)

        // Add wall tiles at edges
        if (x === 0 || y === 0 || x === MAP_WIDTH - 1 || y === MAP_HEIGHT - 1) {
          const wall = this.add.image(isoX, isoY, 'tile_wall')
          wall.setDepth(y + 1)
        }
      }
    }

    // Create puzzle markers with different difficulties
    this.createPuzzleMarkers()
  }

  createPuzzleMarkers() {
    // 8 puzzle markers for Plansza 1: Entropijna Kolej
    const markerData = [
      // Strefa startowa (łatwe)
      { id: 'puzzle_1', x: 5, y: 4, type: 'pattern' as const, difficulty: 'easy' as const },
      { id: 'puzzle_2', x: 4, y: 7, type: 'sequence' as const, difficulty: 'easy' as const },
      // Strefa środkowa (łatwe/średnie)
      { id: 'puzzle_3', x: 10, y: 5, type: 'logic' as const, difficulty: 'easy' as const },
      { id: 'puzzle_4', x: 8, y: 9, type: 'debug' as const, difficulty: 'easy' as const },
      { id: 'puzzle_5', x: 12, y: 8, type: 'pattern' as const, difficulty: 'medium' as const },
      // Strefa zaawansowana (średnie/trudne)
      { id: 'puzzle_6', x: 15, y: 4, type: 'sequence' as const, difficulty: 'medium' as const },
      { id: 'puzzle_7', x: 16, y: 9, type: 'logic' as const, difficulty: 'medium' as const },
      { id: 'puzzle_8', x: 14, y: 12, type: 'debug' as const, difficulty: 'hard' as const },
    ]

    const difficultyColors: Record<string, number> = {
      easy: 0x00ff66,    // Green
      medium: 0xffaa00,  // Orange
      hard: 0xff4444,    // Red
    }

    // Icons for puzzle types
    const puzzleIcons: Record<string, string> = {
      pattern: '◈',   // Diamond pattern
      sequence: '▤',   // Grid/blocks
      logic: '⚡',     // Lightning/circuit
      debug: '⚠',     // Warning/bug
    }

    markerData.forEach(({ id, x, y, type, difficulty }) => {
      const isoX = (x - y) * (TILE_WIDTH / 2) + this.mapOffsetX
      const isoY = (x + y) * (TILE_HEIGHT / 2) + this.mapOffsetY

      // Create container for marker
      const container = this.add.container(isoX, isoY - 16) as PuzzleMarker
      container.markerId = id
      container.puzzleType = type
      container.difficulty = difficulty
      container.gridX = x
      container.gridY = y
      container.isSolved = false
      container.isNearby = false
      container.setDepth(1000)

      // Create marker graphics (circle with icon)
      const bg = this.add.graphics()
      bg.fillStyle(difficultyColors[difficulty], 0.8)
      bg.fillCircle(0, 0, 16)
      bg.lineStyle(2, 0xffffff, 1)
      bg.strokeCircle(0, 0, 16)
      container.add(bg)

      // Add puzzle icon based on type
      const icon = this.add.text(0, 0, puzzleIcons[type] || '?', {
        fontSize: '18px',
        fontStyle: 'bold',
        color: '#ffffff',
      })
      icon.setOrigin(0.5, 0.5)
      container.add(icon)

      // Store data reference
      container.setData('bg', bg)
      container.setData('icon', icon)
      container.setData('baseColor', difficultyColors[difficulty])

      // Pulse animation
      this.tweens.add({
        targets: container,
        scaleX: { from: 0.9, to: 1.1 },
        scaleY: { from: 0.9, to: 1.1 },
        duration: 1000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      })

      // Make interactive
      container.setSize(40, 40)
      container.setInteractive({ useHandCursor: true })
      container.on('pointerdown', () => this.onMarkerClick(container))

      this.puzzleMarkers.push(container)
    })
  }

  onMarkerClick(marker: PuzzleMarker) {
    // Only allow click if player is nearby and marker is not solved
    if (!marker.isNearby || marker.isSolved) return

    // Request puzzle from server with type
    const room = networkClient.getGameRoom()
    if (room) {
      room.send('request_puzzle', {
        difficulty: marker.difficulty,
        type: marker.puzzleType,
      })
    }

    // Store which marker was activated (to mark as solved later)
    this.data.set('activeMarkerId', marker.markerId)
  }

  updateMarkerStates() {
    if (!this.player) return

    const INTERACTION_DISTANCE = 120 // pixels (increased for easier interaction)

    this.puzzleMarkers.forEach((marker) => {
      if (marker.isSolved) return

      // Calculate distance from player to marker (marker.y already includes -16 offset)
      const dx = this.player!.x - marker.x
      const dy = this.player!.y - marker.y
      const distance = Math.sqrt(dx * dx + dy * dy)

      const wasNearby = marker.isNearby
      marker.isNearby = distance < INTERACTION_DISTANCE

      // Update visual if state changed
      if (marker.isNearby !== wasNearby) {
        const bg = marker.getData('bg') as Phaser.GameObjects.Graphics
        const baseColor = marker.getData('baseColor') as number

        bg.clear()
        if (marker.isNearby) {
          // Highlighted state
          bg.fillStyle(0xffffff, 0.9)
          bg.fillCircle(0, 0, 20)
          bg.lineStyle(3, baseColor, 1)
          bg.strokeCircle(0, 0, 20)
        } else {
          // Normal state
          bg.fillStyle(baseColor, 0.8)
          bg.fillCircle(0, 0, 16)
          bg.lineStyle(2, 0xffffff, 1)
          bg.strokeCircle(0, 0, 16)
        }
      }
    })
  }

  markPuzzleSolved(markerId: string) {
    const marker = this.puzzleMarkers.find(m => m.markerId === markerId)
    if (!marker) return

    marker.isSolved = true
    marker.isNearby = false

    // Update visual to "solved" state
    const bg = marker.getData('bg') as Phaser.GameObjects.Graphics
    const icon = marker.getData('icon') as Phaser.GameObjects.Text

    bg.clear()
    bg.fillStyle(0x00ff66, 0.5)
    bg.fillCircle(0, 0, 16)
    bg.lineStyle(2, 0x00ff66, 1)
    bg.strokeCircle(0, 0, 16)

    icon.setText('✓')
    icon.setColor('#00ff66')

    // Stop pulse animation
    this.tweens.killTweensOf(marker)
    marker.setScale(1)
    marker.setAlpha(0.6)
  }

  createLocalPlayer() {
    const state = useGameStore.getState()
    const role = state.selectedRole

    // Start position in center of map
    const startX = 10
    const startY = 7
    const isoX = (startX - startY) * (TILE_WIDTH / 2) + this.mapOffsetX
    const isoY = (startX + startY) * (TILE_HEIGHT / 2) + this.mapOffsetY

    const spriteKey = `player_${role}`
    this.player = this.add.sprite(isoX, isoY - 24, spriteKey) as PlayerSprite
    this.player.playerId = state.mySessionId || 'local'
    this.player.targetX = isoX
    this.player.targetY = isoY - 24
    this.player.setDepth(1000)

    // Add name label
    this.addPlayerLabel(this.player, state.nickname || 'Gracz', role)

    // Send initial position to server
    networkClient.sendMove(this.player.x, this.player.y)
    this.lastSentPosition.x = this.player.x
    this.lastSentPosition.y = this.player.y
  }

  addPlayerLabel(sprite: PlayerSprite, name: string, role: string, isAI: boolean = false) {
    const colors: Record<string, string> = {
      flux: '#00FF66',
      logic: '#4488FF',
      spark: '#FF8844',
      echo: '#AA44FF',
    }

    // AI label styling
    const displayName = isAI ? name : name
    const bgColor = isAI ? '#000000dd' : '#000000aa'
    const fontSize = isAI ? '11px' : '12px'

    const label = this.add.text(0, -40, displayName, {
      fontSize,
      color: colors[role] || '#ffffff',
      backgroundColor: bgColor,
      padding: { x: 4, y: 2 },
    })
    label.setOrigin(0.5, 0.5)

    // Add a subtle glow effect for AI companions
    if (isAI) {
      label.setStroke(colors[role] || '#ffffff', 1)
    }

    // Attach label to sprite
    sprite.setData('label', label)
    sprite.setData('isAI', isAI)
  }

  setupNetworkSync() {
    const room = networkClient.getGameRoom()
    if (!room) return

    const state = useGameStore.getState()
    const mySessionId = state.mySessionId

    // Listen for players_sync (workaround for schema sync issue)
    room.onMessage('players_sync', (data: { players: any[] }) => {
      const currentPlayerIds = new Set<string>()
      console.log(`📡 players_sync: ${data.players.length} players`, data.players.map(p => ({ id: p.sessionId, isAI: p.isAI, role: p.role })))

      data.players.forEach((player) => {
        if (player.sessionId === mySessionId) return
        currentPlayerIds.add(player.sessionId)

        let sprite = this.otherPlayers.get(player.sessionId)

        if (!sprite) {
          const spriteKey = `player_${player.role || 'flux'}`
          sprite = this.add.sprite(player.x, player.y - 24, spriteKey) as PlayerSprite
          sprite.playerId = player.sessionId
          sprite.targetX = player.x
          sprite.targetY = player.y - 24
          sprite.isAI = player.isAI || false
          sprite.setDepth(player.isAI ? 850 : 900)
          sprite.setAlpha(player.isAI ? 0.85 : 0.9)

          console.log(`👤 Created sprite for ${player.sessionId} (isAI: ${sprite.isAI}, role: ${player.role})`)

          // Add label with AI indicator
          this.addPlayerLabel(sprite, player.nickname || 'Gracz', player.role || 'flux', player.isAI || false)
          this.otherPlayers.set(player.sessionId, sprite)

          // Make AI sprites interactive for commands
          if (player.isAI) {
            sprite.setInteractive({ useHandCursor: true })
            sprite.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
              if (pointer.rightButtonDown()) {
                this.showAIContextMenu(sprite!, player.sessionId, pointer.x, pointer.y)
              }
            })
          }
        } else {
          sprite.targetX = player.x
          sprite.targetY = player.y - 24
        }
      })

      // Remove sprites for players who left
      this.otherPlayers.forEach((sprite, sessionId) => {
        if (!currentPlayerIds.has(sessionId)) {
          const label = sprite.getData('label') as Phaser.GameObjects.Text
          if (label) label.destroy()
          sprite.destroy()
          this.otherPlayers.delete(sessionId)
        }
      })
    })

    // Listen for real-time position updates
    room.onMessage('player_moved', (data: { sessionId: string; x: number; y: number }) => {
      if (data.sessionId === mySessionId) return
      const sprite = this.otherPlayers.get(data.sessionId)
      if (sprite) {
        sprite.targetX = data.x
        sprite.targetY = data.y - 24
      }
    })

    // Listen for puzzle solved to mark markers as completed
    room.onMessage('puzzle_solved', () => {
      const activeMarkerId = this.data.get('activeMarkerId') as string
      if (activeMarkerId) {
        this.markPuzzleSolved(activeMarkerId)
        this.data.remove('activeMarkerId')
      }
    })

    // Request fresh players_sync now that listeners are registered
    room.send('request_players_sync')

    // Handle drop-in (player took over AI mid-game)
    room.onMessage('drop_in_success', (data: { role: string; x: number; y: number }) => {
      // Reposition local player to AI's position
      if (this.player) {
        this.player.x = data.x
        this.player.y = data.y - 24
        this.lastSentPosition.x = data.x
        this.lastSentPosition.y = data.y - 24
        console.log(`🔄 Repositioned to AI position: (${data.x}, ${data.y})`)
      }
    })

    // Handle drop-out (another player left)
    room.onMessage('player_drop_out', (data: { sessionId: string; aiId: string }) => {
      // The player sprite will be handled by players_sync
      // But we need to remove the old sprite immediately
      const oldSprite = this.otherPlayers.get(data.sessionId)
      if (oldSprite) {
        const label = oldSprite.getData('label') as Phaser.GameObjects.Text
        if (label) label.destroy()
        oldSprite.destroy()
        this.otherPlayers.delete(data.sessionId)
      }
    })
  }

  update(_time: number, delta: number) {
    if (!this.player || !this.cursors || !this.wasd) return

    // Handle input
    let velocityX = 0
    let velocityY = 0

    // Arrow keys
    if (this.cursors.left.isDown || this.wasd.A.isDown) {
      velocityX = -PLAYER_SPEED
    } else if (this.cursors.right.isDown || this.wasd.D.isDown) {
      velocityX = PLAYER_SPEED
    }

    if (this.cursors.up.isDown || this.wasd.W.isDown) {
      velocityY = -PLAYER_SPEED
    } else if (this.cursors.down.isDown || this.wasd.S.isDown) {
      velocityY = PLAYER_SPEED
    }

    // Apply movement
    if (velocityX !== 0 || velocityY !== 0) {
      // Normalize diagonal movement
      if (velocityX !== 0 && velocityY !== 0) {
        velocityX *= 0.707
        velocityY *= 0.707
      }

      this.player.x += velocityX * (delta / 1000)
      this.player.y += velocityY * (delta / 1000)
    }

    // Update player label position
    const label = this.player.getData('label') as Phaser.GameObjects.Text
    if (label) {
      label.x = this.player.x
      label.y = this.player.y - 40
    }

    // Update depth based on Y position (for proper overlapping)
    this.player.setDepth(Math.floor(this.player.y) + 1000)

    // Send position to server (throttled)
    this.sendPositionTimer += delta
    if (this.sendPositionTimer > 50) { // 20 times per second max
      this.sendPositionTimer = 0

      const dx = Math.abs(this.player.x - this.lastSentPosition.x)
      const dy = Math.abs(this.player.y - this.lastSentPosition.y)

      if (dx > 2 || dy > 2) {
        networkClient.sendMove(this.player.x, this.player.y)
        this.lastSentPosition.x = this.player.x
        this.lastSentPosition.y = this.player.y
      }
    }

    // Smoothly move other players to their target positions
    this.otherPlayers.forEach((sprite) => {
      const dx = sprite.targetX - sprite.x
      const dy = sprite.targetY - sprite.y

      if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
        sprite.x += dx * 0.15
        sprite.y += dy * 0.15
      }

      // Update label
      const otherLabel = sprite.getData('label') as Phaser.GameObjects.Text
      if (otherLabel) {
        otherLabel.x = sprite.x
        otherLabel.y = sprite.y - 40
      }

      sprite.setDepth(Math.floor(sprite.y) + 900)
    })

    // Update puzzle marker states (highlight when nearby)
    this.updateMarkerStates()

    // Handle AI selection keys
    this.handleAISelection()
  }

  handleAISelection() {
    if (!this.aiNumberKeys) return

    const aiList = this.getAIList()

    if (Phaser.Input.Keyboard.JustDown(this.aiNumberKeys.one) && aiList[0]) {
      this.selectAI(aiList[0].playerId)
    } else if (Phaser.Input.Keyboard.JustDown(this.aiNumberKeys.two) && aiList[1]) {
      this.selectAI(aiList[1].playerId)
    } else if (Phaser.Input.Keyboard.JustDown(this.aiNumberKeys.three) && aiList[2]) {
      this.selectAI(aiList[2].playerId)
    }
  }

  getAIList(): PlayerSprite[] {
    const aiSprites: PlayerSprite[] = []
    this.otherPlayers.forEach((sprite, id) => {
      // Check both the sprite's isAI flag and if the ID starts with "ai_"
      if (sprite.isAI || id.startsWith('ai_')) {
        aiSprites.push(sprite)
      }
    })
    // Sort by role for consistent ordering
    aiSprites.sort((a, b) => a.playerId.localeCompare(b.playerId))
    return aiSprites
  }

  selectAI(aiId: string | null) {
    // Clear previous selection highlight
    if (this.selectedAI) {
      const prevSprite = this.otherPlayers.get(this.selectedAI)
      if (prevSprite) {
        prevSprite.clearTint()
      }
    }

    this.selectedAI = aiId

    // Add selection highlight
    if (aiId) {
      const sprite = this.otherPlayers.get(aiId)
      if (sprite) {
        sprite.setTint(0xffffff)
        // Show selection indicator
        console.log(`🤖 Selected AI: ${aiId}`)
      }
    }
  }

  handleRightClick(pointer: Phaser.Input.Pointer) {
    console.log(`🖱️ Right-click detected at screen (${pointer.x}, ${pointer.y})`)

    // Close any existing menu
    this.closeAIContextMenu()

    // If an AI is selected, send move command
    if (this.selectedAI) {
      const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y)

      // Check if clicking on a marker
      const clickedMarker = this.puzzleMarkers.find(marker => {
        const dx = worldPoint.x - marker.x
        const dy = worldPoint.y - marker.y
        return Math.sqrt(dx * dx + dy * dy) < 40
      })

      if (clickedMarker && !clickedMarker.isSolved) {
        // Send investigate command
        this.sendAIInvestigate(this.selectedAI, clickedMarker)
      } else {
        // Send move command
        this.sendAIMove(this.selectedAI, worldPoint.x, worldPoint.y)
      }

      // Deselect after command
      this.selectAI(null)
      return
    }

    // If no AI selected, show context menu for map if clicking on empty space
    const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y)

    // Check if clicking on a marker
    const clickedMarker = this.puzzleMarkers.find(marker => {
      const dx = worldPoint.x - marker.x
      const dy = worldPoint.y - marker.y
      return Math.sqrt(dx * dx + dy * dy) < 40
    })

    if (clickedMarker && !clickedMarker.isSolved) {
      this.showMarkerContextMenu(clickedMarker, pointer.x, pointer.y)
    } else {
      this.showMapContextMenu(worldPoint.x, worldPoint.y, pointer.x, pointer.y)
    }
  }

  showAIContextMenu(sprite: PlayerSprite, aiId: string, screenX: number, screenY: number) {
    console.log(`📋 showAIContextMenu for ${aiId}`)

    const label = sprite.getData('label') as Phaser.GameObjects.Text
    const aiName = label?.text || 'AI'

    useGameStore.getState().showAIContextMenu({
      type: 'ai',
      screenX,
      screenY,
      aiId,
      aiList: [{ id: aiId, name: aiName, role: '' }]
    })
  }

  showMapContextMenu(worldX: number, worldY: number, screenX: number, screenY: number) {
    const aiList = this.getAIList()
    console.log(`📋 showMapContextMenu called at (${screenX}, ${screenY}) with ${aiList.length} AI`)
    if (aiList.length === 0) {
      console.log('❌ No AI companions available for context menu')
      return
    }

    // Build AI list for React menu
    const aiListData = aiList.map(ai => {
      const label = ai.getData('label') as Phaser.GameObjects.Text
      const state = useGameStore.getState().players[ai.playerId]
      return {
        id: ai.playerId,
        name: label?.text || 'AI',
        role: state?.role || 'flux'
      }
    })

    useGameStore.getState().showAIContextMenu({
      type: 'map',
      screenX,
      screenY,
      worldX,
      worldY,
      aiList: aiListData
    })

    console.log(`✅ React context menu triggered with ${aiListData.length} AI`)
  }

  showMarkerContextMenu(marker: PuzzleMarker, screenX: number, screenY: number) {
    const aiList = this.getAIList()
    console.log(`📋 showMarkerContextMenu for ${marker.markerId} with ${aiList.length} AI`)
    if (aiList.length === 0) return

    // Build AI list for React menu
    const aiListData = aiList.map(ai => {
      const label = ai.getData('label') as Phaser.GameObjects.Text
      const state = useGameStore.getState().players[ai.playerId]
      return {
        id: ai.playerId,
        name: label?.text || 'AI',
        role: state?.role || 'flux'
      }
    })

    useGameStore.getState().showAIContextMenu({
      type: 'marker',
      screenX,
      screenY,
      worldX: marker.x,
      worldY: marker.y + 16,
      markerId: marker.markerId,
      markerType: marker.puzzleType,
      markerDifficulty: marker.difficulty,
      aiList: aiListData
    })

    console.log(`✅ React marker context menu triggered`)
  }

  closeAIContextMenu() {
    useGameStore.getState().closeAIContextMenu()
  }

  sendAIMove(aiId: string, x: number, y: number) {
    const room = networkClient.getGameRoom()
    if (room) {
      room.send('ai_move', { aiId, x, y })
      console.log(`🤖 Sending AI ${aiId} to (${x.toFixed(0)}, ${y.toFixed(0)})`)
    }
  }

  sendAIInvestigate(aiId: string, marker: PuzzleMarker) {
    const room = networkClient.getGameRoom()
    if (room) {
      room.send('ai_investigate', {
        aiId,
        markerId: marker.markerId,
        x: marker.x,
        y: marker.y + 16, // Offset back since marker.y has -16 offset
      })
      console.log(`🤖 Sending AI ${aiId} to investigate ${marker.markerId}`)

      // Store marker data for remote puzzle request
      this.data.set('aiInvestigating', {
        aiId,
        markerId: marker.markerId,
        puzzleType: marker.puzzleType,
        difficulty: marker.difficulty,
      })
    }
  }

  sendAIReturn(aiId: string) {
    const room = networkClient.getGameRoom()
    if (room) {
      room.send('ai_return', { aiId })
      console.log(`🤖 Calling AI ${aiId} back`)
    }
  }
}
